/**
 * The PPSR Register report.
 *
 * Winnie, 18 September: "One row per vehicle, showing the full lifecycle rather than just the
 * discharge half... A report to view and confirm that PPSR checks and discharges have
 * actually gone through — this matters more once discharge is a live API call rather than an
 * internal flag, since 'automatic' doesn't mean 'unfailable.'"
 *
 * The three stages come from three different places, which is the reason this report is worth
 * having: the CHECK lives on the deal (`ppsr_status`, written by the search), the
 * REGISTRATION and the DISCHARGE live on `ppsr_registrations`. Nobody could previously see all
 * three together, so a vehicle that was checked, lodged and never discharged looked fine on
 * every screen it appeared on.
 */
import { supabase } from '../lib/supabase'
import { DateRange, ALL_TIME, inRange } from '../lib/dateRange'
import { DischargeStatus } from './ppsrDischargeService'

export type CheckStatus = 'Clear' | 'ExistingInterestFound' | 'NotChecked'

export const CHECK_LABEL: Record<CheckStatus, string> = {
  Clear: 'Clear',
  ExistingInterestFound: 'Existing interest found',
  NotChecked: 'Not yet checked',
}

export const DISCHARGE_LABEL: Record<DischargeStatus, string> = {
  NotApplicable: 'N/A',
  Pending: 'Pending',
  Discharged: 'Discharged',
  Failed: 'Failed',
}

export interface PpsrRegisterRow {
  dealId: string
  vin: string
  vehicle: string
  stockNumber: string
  soldDate: string | null
  paymentClearedDate: string | null
  checkStatus: CheckStatus
  checkedAt: string | null
  /** Null when nothing was ever lodged against this vehicle. */
  lodgedAt: string | null
  registrationNumber: string | null
  dischargeStatus: DischargeStatus
  dischargedAt: string | null
  dischargeReference: string | null
  dischargeError: string | null
}

export interface PpsrRegisterSummary {
  checkedThisPeriod: number
  existingInterestsFound: number
  totalLodgements: number
  successfullyDischarged: number
  failedDischarges: number
}

export interface PpsrRegisterReport {
  rows: PpsrRegisterRow[]
  summary: PpsrRegisterSummary
}

const vehicleOf = (d: Record<string, unknown>): string =>
  [d.autograb_year ?? d.vehicle_year, d.autograb_make || d.vehicle_make, d.autograb_model || d.vehicle_model]
    .filter(Boolean)
    .join(' ')
    .trim() || 'Vehicle'

/**
 * How a PPSR search result reads as a check status.
 *
 * `deals.ppsr_status` carries the search's own vocabulary. Anything that is not recognisably
 * clear and not recognisably encumbered is reported as NOT CHECKED rather than being guessed
 * into one of the two — a report whose job is to prove a check happened must not invent one.
 */
export function checkStatusOf(raw?: string | null, checkedAt?: string | null): CheckStatus {
  const v = String(raw || '').trim().toLowerCase()
  if (!v && !checkedAt) return 'NotChecked'
  if (v === 'clear' || v === 'clear_title' || v === 'no_interest' || v === 'passed') return 'Clear'
  if (
    v.includes('encumb') ||
    v.includes('interest') ||
    v.includes('finance') ||
    v === 'flagged' ||
    v === 'failed'
  ) {
    return 'ExistingInterestFound'
  }
  return checkedAt ? 'Clear' : 'NotChecked'
}

export async function loadPpsrRegisterReport(range: DateRange = ALL_TIME): Promise<PpsrRegisterReport> {
  const [dealsRes, lodgeRes] = await Promise.all([
    supabase
      .from('deals')
      .select(
        'id, stock_number, autograb_vin, vehicle_vin, autograb_year, autograb_make, autograb_model, ' +
          'vehicle_year, vehicle_make, vehicle_model, ppsr_status, ppsr_check_date, sold_at, sale_date, ' +
          'settled_at, ppsr_discharged, deal_status',
      )
      .order('created_at', { ascending: false }),
    supabase.from('ppsr_registrations').select('*'),
  ])
  if (dealsRes.error) throw dealsRes.error
  if (lodgeRes.error) throw lodgeRes.error

  /* Most recent lodgement per deal — "0 or 1 per Vehicle", but history is allowed to disagree
     and the report should not throw when it does. */
  const byDeal = new Map<string, Record<string, unknown>>()
  for (const raw of (lodgeRes.data || []) as unknown as Record<string, unknown>[]) {
    const key = String(raw.deal_id)
    const prev = byDeal.get(key)
    if (!prev || String(raw.submitted_at || '') > String(prev.submitted_at || '')) byDeal.set(key, raw)
  }

  const rows: PpsrRegisterRow[] = ((dealsRes.data || []) as unknown as Record<string, unknown>[]).map((d) => {
    const lodgement = byDeal.get(String(d.id)) || null
    const checkedAt = (d.ppsr_check_date as string) || null
    return {
      dealId: String(d.id),
      vin: String(d.autograb_vin || d.vehicle_vin || ''),
      vehicle: vehicleOf(d),
      stockNumber: String(d.stock_number || ''),
      soldDate: (d.sold_at as string) || (d.sale_date as string) || null,
      paymentClearedDate: (d.settled_at as string) || null,
      checkStatus: checkStatusOf(d.ppsr_status as string, checkedAt),
      checkedAt,
      lodgedAt: lodgement ? (lodgement.submitted_at as string) || null : null,
      registrationNumber: lodgement ? (lodgement.registration_number as string) || null : null,
      /* No lodgement means there is nothing to discharge — N/A, not Pending. Reading it as
         Pending would put every car Direct Auto never registered onto a list of outstanding
         compliance work. */
      dischargeStatus: lodgement
        ? ((lodgement.discharge_status as DischargeStatus) || 'Pending')
        : 'NotApplicable',
      dischargedAt: lodgement ? (lodgement.discharged_at as string) || null : null,
      dischargeReference: lodgement ? (lodgement.discharge_reference as string) || null : null,
      dischargeError: lodgement ? (lodgement.discharge_error as string) || null : null,
    }
  })

  /* The date range filters what is COUNTED as checked in the period, per the card Winnie
     named ("Vehicles checked this period"); the rows themselves are not hidden, because a
     failed discharge from last quarter is exactly the thing this report exists to surface. */
  const inPeriod = (iso: string | null) => (iso ? inRange(iso.slice(0, 10), range) : false)

  return {
    rows,
    summary: {
      checkedThisPeriod: rows.filter((r) => r.checkStatus !== 'NotChecked' && inPeriod(r.checkedAt)).length,
      existingInterestsFound: rows.filter((r) => r.checkStatus === 'ExistingInterestFound').length,
      totalLodgements: rows.filter((r) => !!r.lodgedAt).length,
      successfullyDischarged: rows.filter((r) => r.dischargeStatus === 'Discharged').length,
      failedDischarges: rows.filter((r) => r.dischargeStatus === 'Failed').length,
    },
  }
}

const cell = (v: unknown): string => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export function ppsrRegisterCsv(rows: PpsrRegisterRow[]): string {
  const head = [
    'VIN',
    'Vehicle',
    'Stock #',
    'Sold date',
    'Payment cleared',
    'Check',
    'Checked at',
    'Registration',
    'Lodged at',
    'Discharge',
    'Discharged at',
    'Confirmation reference',
  ]
  const body = rows.map((r) =>
    [
      r.vin,
      r.vehicle,
      r.stockNumber,
      r.soldDate ? r.soldDate.slice(0, 10) : '',
      r.paymentClearedDate ? r.paymentClearedDate.slice(0, 10) : '',
      CHECK_LABEL[r.checkStatus],
      r.checkedAt ? r.checkedAt.slice(0, 10) : '',
      r.registrationNumber ? 'Lodged' : 'None',
      r.lodgedAt ? r.lodgedAt.slice(0, 10) : '',
      DISCHARGE_LABEL[r.dischargeStatus],
      r.dischargedAt ? r.dischargedAt.slice(0, 10) : '',
      r.dischargeReference || '',
    ].map(cell).join(','),
  )
  return [head.join(','), ...body].join('\n')
}
